export const reportingKeys = {
  all: ['reporting'] as const,
  dashboards: () => [...reportingKeys.all, 'dashboards'] as const,
  dashboardList: (orgId: string) => [...reportingKeys.dashboards(), orgId] as const,
  dashboard: (dashboardId: string) => [...reportingKeys.all, 'dashboard', dashboardId] as const,
  widgetData: () => [...reportingKeys.all, 'widget-data'] as const,
  /** The agentic home's Trending panel: outside `widgetData()`, so a direct mark of that prefix skips it. */
  homeTrends: () => [...reportingKeys.all, 'home-trends'] as const,
  homeTrendsFor: (orgId: string, userId: string) =>
    [...reportingKeys.homeTrends(), orgId, userId] as const,
  // `orgId` is a scope dimension of the response, not decoration: the RPC filters
  // on the caller's organisation and the cache is NOT cleared on org switch
  // (see use-organization-switching). Widget ids are `gen_random_uuid()` today, so
  // omitting it happened to be safe — one synthetic/templated widget id would make
  // it a cross-tenant render. See .claude/rules/frontend.md → State Management.
  // `widgetType` is a scope dimension too: the drill-down asks the same widget
  // and the same config for `kpi_rows` rather than `kpi`, and the two payloads
  // are different shapes. Without it both land on one entry and whichever
  // resolved last wins — the tile renders a row list, or the dialog a scalar.
  // `userId` and `dataSource` are scope dimensions of the RESPONSE, not just of
  // the request: `my_workspace` widgets filter on the caller, and a widget's
  // data source can be edited without its config hash changing. Leaving either
  // out serves one viewer's numbers to another, or the old source's numbers
  // after a source switch.
  widget: (
    orgId: string,
    userId: string,
    widgetId: string,
    dataSource: string,
    configHash: string,
    widgetType: string,
  ) =>
    [
      ...reportingKeys.widgetData(),
      orgId,
      userId,
      widgetId,
      dataSource,
      configHash,
      widgetType,
    ] as const,
  /**
   * Prefix for every hydrated drill-down row-detail entry. Share revocation
   * removes the whole prefix rather than matching ids: the cached map is the
   * only thing standing between a revoked title and the screen, and with
   * refetch-on-mount/focus/reconnect disabled app-wide nothing else would
   * re-check it inside its staleTime.
   */
  drilldownRowDetailAll: () => [...reportingKeys.all, 'drilldown-row-detail'] as const,
  // `userId` is not decoration here: unlike the widget aggregates, this entry
  // holds rows read under the caller's OWN RLS, so two members of one org get
  // legitimately different answers for the same ids. The ids are part of the
  // key too — the map answers "titles for exactly these rows", and a different
  // page of the same measure is a different answer.
  drilldownRowDetail: (orgId: string, userId: string, dataSource: string, ids: readonly string[]) =>
    [
      ...reportingKeys.all,
      'drilldown-row-detail',
      orgId,
      userId,
      dataSource,
      [...ids].sort((a, b) => a.localeCompare(b)),
    ] as const,
  filterValues: (orgId: string, dataSource: string, filterField: string | undefined) =>
    [...reportingKeys.all, 'filter-values', orgId, dataSource, filterField] as const,
};
